import { ZONES, clampBox, type Box, type Zone } from './geometry';

// The studio's view of a recipe: the same shape the API keeps (lib/lab/recipe.ts
// there is the authority); here only what the canvas and the inspector read.

export type PlateKind = 'none' | 'still' | 'streaks' | 'worldmap' | 'starburst' | 'bokeh' | 'aurora' | 'grid';
export type ElementType = 'station' | 'band' | 'logo' | 'social' | 'ticker' | 'bug' | 'text' | 'picture' | 'shape' | 'callout' | 'progress';
export type Motion = 'none' | 'slide' | 'wipe' | 'fade';
export type MessageKind = 'topic' | 'follow' | 'title' | 'hashtag' | 'custom';
export const PLATE_KINDS: PlateKind[] = ['none', 'streaks', 'worldmap', 'starburst', 'bokeh', 'aurora', 'grid'];
export const ELEMENT_TYPES: ElementType[] = ['station', 'band', 'logo', 'social', 'ticker', 'bug', 'text', 'picture', 'shape', 'callout', 'progress'];
export const MOTIONS: Motion[] = ['none', 'slide', 'wipe', 'fade'];
export const MESSAGE_KINDS: MessageKind[] = ['topic', 'follow', 'title', 'hashtag', 'custom'];
export const SOCIAL_IDS = ['youtube', 'instagram', 'facebook', 'tiktok', 'x', 'threads', 'website'] as const;
export const SECONDS_CHOICES = [6, 8, 10, 12, 14, 16, 18, 20];

export interface Message { kind: MessageKind; text?: string; sub?: string; handle?: string; platforms?: string[]; tag?: string }
export interface Element {
  id: string; type: ElementType; box: Box; zone?: Zone;
  style: { fill?: string; ink?: string; accent?: string; alpha?: number; radius?: number; font?: 'sans' | 'serif' | 'mono'; size?: number; bold?: boolean; caps?: boolean; stars?: number; rail?: 'none' | 'left' | 'top' };
  content: { text?: string; sub?: string; messages?: Message[]; logoAssetId?: string; assetId?: string; clock?: boolean; platform?: string; handle?: string; ticker?: string; speed?: number; shape?: 'rect' | 'pill' | 'circle'; pointer?: 'left' | 'right' | 'top' | 'bottom'; progress?: 'fill' | 'drain' };
  motion: { enter: Motion; exit: Motion };
}
export interface Plate { kind: PlateKind; ground: string; accent: string; second?: string; speed: number; density: number; seed: number; assetId?: string }
export interface Frames { count: number; line: string; width: number; plates: 'band' | 'pill' | 'none' }
export interface Recipe { version: 1; canvas: { w: number; h: number }; seconds: number; fps: number; plate: Plate | null; elements: Element[]; frames: Frames | null }

export interface Look {
  id: string; brandId: string; kind: 'plate' | 'rig' | 'frame' | 'scene'; name: string; source: string; recipe: Recipe; shared: boolean;
  status: 'draft' | 'rendering' | 'ready' | 'error'; error: string | null;
  assets: { plate: string | null; overlay: string | null; preview: string | null; poster: string | null };
  createdAt: string; updatedAt: string;
}

export function blankRecipe(accent: string, ground: string): Recipe {
  return { version: 1, canvas: { w: 1920, h: 1080 }, seconds: 12, fps: 30, plate: { kind: 'aurora', ground, accent, speed: 1, density: 1, seed: 7 }, elements: [], frames: null };
}

/** The next free element id. */
export function nextId(r: Recipe): string {
  let n = 1; const have = new Set(r.elements.map((e) => e.id));
  while (have.has(`e${n}`)) n++;
  return `e${n}`;
}

/** A new element of a type, in a zone's box, with sensible content. */
export function newElement(r: Recipe, type: ElementType, zone: Zone, showName: string): Element {
  const base: Element = { id: nextId(r), type, box: { ...ZONES[zone] }, zone, style: { alpha: 0.9, radius: 8 }, content: {}, motion: { enter: 'slide', exit: 'slide' } };
  if (type === 'band') base.content.messages = [{ kind: 'title', text: showName, sub: 'on Rookery' }];
  if (type === 'ticker') { base.box = { ...ZONES.foot }; base.zone = 'foot'; base.content.ticker = `${showName} · on Rookery`; base.content.speed = 110; base.motion = { enter: 'none', exit: 'none' }; }
  if (type === 'bug') { base.box = { x: ZONES['bottom-right'].x + 260, y: ZONES['bottom-right'].y + 84, w: 160, h: 56 }; base.content.text = showName.slice(0, 12); base.motion = { enter: 'fade', exit: 'none' }; }
  if (type === 'station') { base.box = { ...ZONES['top-left'], w: 300, h: 130 }; base.style = { stars: 2, radius: 6 }; base.content = { text: showName, clock: true }; base.motion = { enter: 'slide', exit: 'none' }; }
  if (type === 'logo') { base.box = { ...ZONES['top-right'], w: 310, h: 130 }; base.content.text = showName; base.motion = { enter: 'slide', exit: 'none' }; }
  if (type === 'text') base.content.text = 'Your words here';
  if (type === 'social') base.box = { x: ZONES['lower-third'].x, y: ZONES['lower-third'].y + 30, w: 150, h: 130 };
  // 194: the Canva-shaped three.
  if (type === 'shape') { base.box = { x: 64, y: 64, w: 520, h: 72 }; base.style = { alpha: 1, radius: 36 }; base.content.shape = 'pill'; base.motion = { enter: 'wipe', exit: 'fade' }; }
  if (type === 'callout') { base.box = { x: 1100, y: 300, w: 520, h: 120 }; base.style = { alpha: 0.95, radius: 10, size: 40 }; base.content = { text: 'Look here', pointer: 'left' }; base.motion = { enter: 'fade', exit: 'fade' }; }
  if (type === 'progress') { base.box = { x: 64, y: 1010, w: 1792, h: 14 }; base.style = { alpha: 0.6, radius: 7 }; base.content.progress = 'fill'; base.motion = { enter: 'none', exit: 'none' }; }
  return base;
}

/** Move or resize an element, kept inside the canvas and snapped to a zone when close. */
export function moveElement(r: Recipe, id: string, box: Box, snapPx = 24): Recipe {
  const clamped = clampBox(box);
  let zone: Zone | undefined;
  for (const [z, zb] of Object.entries(ZONES) as [Zone, Box][]) {
    if (Math.abs(clamped.x - zb.x) <= snapPx && Math.abs(clamped.y - zb.y) <= snapPx && Math.abs(clamped.w - zb.w) <= snapPx * 2 && Math.abs(clamped.h - zb.h) <= snapPx * 2) { zone = z; break; }
  }
  return { ...r, elements: r.elements.map((e) => (e.id === id ? { ...e, box: zone ? { ...ZONES[zone] } : clamped, ...(zone ? { zone } : { zone: undefined }) } : e)) };
}

/** 194: a shape's corner — a pill is half its height, a circle is round, the rest is the style's radius. */
export function shapeRadius(el: Element, box: Box): number {
  if (el.type !== 'shape') return el.style.radius ?? 8;
  return el.content.shape === 'circle' ? Math.max(box.w, box.h) : el.content.shape === 'pill' ? box.h / 2 : (el.style.radius ?? 8);
}

/** 194: how far along the loop a progress bar is at t — filling, or draining. The same at 0 and at the end, so the loop is seamless. */
export function progressFraction(r: Recipe, t: number, mode: 'fill' | 'drain' = 'fill'): number {
  const f = r.seconds > 0 ? ((t % r.seconds) + r.seconds) % r.seconds / r.seconds : 0;
  return mode === 'drain' ? 1 - f : f;
}
